import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { X, ArrowUpRight, Wrench, Sparkles, Lock, ExternalLink, FileText, ChevronDown } from "lucide-react";
import { CASE_STUDIES, SUPPORTING_PORTFOLIO } from "../data/content";
import { Eyebrow, Reveal } from "./shared";
import { PlanetDot } from "./motifs";

/**
 * Scroll-linked warm parallax for the case-study cover image.
 * - Vertical drift ±18px as the card travels through the viewport.
 * - Slow horizontal breathing (kenburns) layered on top.
 * - Uses transform-only so it never triggers layout thrash.
 */
function ParallaxCover({ src, alt, driftClass = "kenburns" }) {
  const wrap = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrap,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <div ref={wrap} className="absolute inset-0 overflow-hidden">
      <motion.div style={{ y }} className="w-full h-[125%] -mt-[6%]">
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover ${driftClass}`}
          loading="lazy"
        />
      </motion.div>
    </div>
  );
}

function Modal({ study, onClose }) {
  const scrollRef = useRef(null);
  const [showCue, setShowCue] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (study) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [study, onClose]);

  // Toggle "scroll for more" cue based on scroll position + overflow
  useEffect(() => {
    if (!study) return;
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const hasOverflow = el.scrollHeight - el.clientHeight > 8;
      const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 24;
      setShowCue(hasOverflow && !nearBottom);
    };
    // slight delay so layout settles after modal open animation
    const t = setTimeout(update, 250);
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      clearTimeout(t);
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [study]);

  return (
    <AnimatePresence>
      {study && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="casestudy-modal"
        >
          <div className="fixed inset-0 bg-warmdark/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-ivory rounded-[1.4rem] w-full max-w-4xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-hidden border border-border flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label={study.title}
          >
            <button
              onClick={onClose}
              data-testid="casestudy-close"
              aria-label="Close"
              className="absolute top-4 right-4 z-20 w-10 h-10 grid place-items-center rounded-full bg-ivory/90 hover:bg-linen text-caramel border border-border"
            >
              <X size={20} />
            </button>
            <div className="relative h-56 sm:h-72 shrink-0">
              <img src={study.preview} alt={study.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-warmdark/80 to-transparent" />
              <div className="absolute bottom-5 left-6 right-6">
                <div className="font-mono-tech text-[11px] tracking-widest uppercase text-honey">{study.subtitle}</div>
                <h3 className="font-display font-light text-ivory text-3xl sm:text-4xl mt-1">{study.title}</h3>
              </div>
            </div>
            <div
              ref={scrollRef}
              className="p-6 sm:p-9 space-y-8 overflow-y-auto overscroll-contain flex-1"
              data-testid="casestudy-scroll"
              data-lenis-prevent
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-mono-tech text-xs tracking-widest uppercase text-terracotta mb-2">The Brief</h4>
                  <p className="text-caramel/85 leading-relaxed">{study.brief}</p>
                </div>
                <div>
                  <h4 className="font-mono-tech text-xs tracking-widest uppercase text-terracotta mb-2">The Challenge</h4>
                  <p className="text-caramel/85 leading-relaxed">{study.challenge}</p>
                </div>
              </div>

              <div>
                <h4 className="font-mono-tech text-xs tracking-widest uppercase text-terracotta mb-4">What I Built</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {study.built.map((b) => (
                    <div key={b.t} className="rounded-xl bg-linen border border-border p-4">
                      <div className="font-semibold text-caramel">{b.t}</div>
                      <div className="text-sm text-caramel/70 mt-1 leading-relaxed">{b.d}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-mono-tech text-xs tracking-widest uppercase text-terracotta mb-4">Notes & Approach</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {study.faq.map((f) => (
                    <div key={f.q} className="rounded-xl bg-sage/15 border border-sage/30 p-4">
                      <div className="font-semibold text-forest">{f.q}</div>
                      <div className="text-sm text-caramel/75 mt-1 leading-relaxed">{f.a}</div>
                    </div>
                  ))}
                </div>
              </div>

              {study.samples && study.samples.length > 0 && (
                <div data-testid="casestudy-samples">
                  <h4 className="font-mono-tech text-xs tracking-widest uppercase text-terracotta mb-4">Sample Materials</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {study.samples.map((s) => {
                      const isFile = s.kind === "file";
                      const Icon = isFile ? FileText : ExternalLink;
                      return (
                        <a
                          key={s.url}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...(isFile ? { download: "" } : {})}
                          data-testid={`casestudy-sample-${s.kind}`}
                          className="group flex items-start gap-3 rounded-xl bg-linen border border-border p-4 hover:border-honey/70 hover:bg-softgold/40 transition-colors"
                        >
                          <span className="mt-0.5 w-9 h-9 grid place-items-center rounded-full bg-warmdark text-honey shrink-0">
                            <Icon size={16} />
                          </span>
                          <div className="min-w-0">
                            <div className="font-mono-tech text-[10px] tracking-widest uppercase text-brass">
                              {isFile ? "Attachment · PDF" : "External · Live preview"}
                            </div>
                            <div className="font-semibold text-caramel mt-0.5 leading-snug truncate group-hover:text-terracotta transition-colors">
                              {s.label}
                            </div>
                            <div className="text-xs text-warmmuted mt-1 flex items-center gap-1">
                              {isFile ? "Download PDF" : "Open in a new tab"} <ArrowUpRight size={12} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="rounded-xl bg-softgold p-6">
                <h4 className="font-mono-tech text-xs tracking-widest uppercase text-warmdark/70 mb-1">The Result</h4>
                <p className="font-display text-2xl sm:text-3xl text-warmdark font-light">{study.result}</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-mono-tech text-xs tracking-widest uppercase text-terracotta mb-3 flex items-center gap-2"><Wrench size={14} /> Tools Used</h4>
                  <div className="flex flex-wrap gap-2">{study.tools.map((t) => <span key={t} className="font-mono-tech text-xs rounded-full px-3 py-1.5 bg-linen border border-border text-caramel">{t}</span>)}</div>
                </div>
                <div>
                  <h4 className="font-mono-tech text-xs tracking-widest uppercase text-terracotta mb-3 flex items-center gap-2"><Sparkles size={14} /> Skills Demonstrated</h4>
                  <div className="flex flex-wrap gap-2">{study.skills.map((t) => <span key={t} className="font-mono-tech text-xs rounded-full px-3 py-1.5 bg-sage/20 border border-sage/40 text-forest">{t}</span>)}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-warmmuted text-sm border-t border-border pt-5">
                <Lock size={14} /> {study.confidentiality}
              </div>
            </div>

            {/* Scroll-for-more cue: gradient fade + label pinned inside the panel */}
            <AnimatePresence>
              {showCue && (
                <motion.div
                  key="scroll-cue"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-24 flex items-end justify-center pb-3 bg-gradient-to-t from-ivory via-ivory/85 to-transparent"
                  data-testid="casestudy-scroll-cue"
                  aria-hidden
                >
                  <motion.div
                    animate={{ y: [0, 4, 0] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                    className="flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-brass"
                  >
                    Scroll for more <ChevronDown size={12} className="text-brass" />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Portfolio() {
  const [active, setActive] = useState(null);
  return (
    <section id="portfolio" className="relative py-24 sm:py-32 bg-linen overflow-hidden">
      <div className="pointer-events-none absolute inset-0 hairline-grid opacity-30" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal><Eyebrow n={6} className="mb-12">Portfolio</Eyebrow></Reveal>

        {/* Two featured case studies side-by-side */}
        <div className="grid md:grid-cols-2 gap-6" data-testid="case-studies-grid">
          {CASE_STUDIES.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.1}>
              <button
                onClick={() => setActive(s)}
                data-testid={`casestudy-open-${s.key}`}
                data-cosmic="true"
                className="group text-left w-full h-full rounded-[1.4rem] overflow-hidden border border-border bg-ivory hover:-translate-y-1 transition-transform duration-300 shadow-[0_10px_40px_rgba(74,51,34,0.06)] hover:shadow-[0_24px_60px_rgba(74,51,34,0.14)] observatory-frame"
              >
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <ParallaxCover
                    src={s.preview}
                    alt={s.title}
                    driftClass={i % 2 === 0 ? "kenburns" : "kenburns kenburns-b"}
                  />
                  <span className="absolute top-4 left-4 z-10 flex items-center gap-2 font-mono-tech text-[10px] tracking-widest uppercase bg-ivory/90 text-terracotta rounded-full px-3 py-1.5 border border-brass/30">
                    <PlanetDot /> Featured case study · {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="p-6 sm:p-7">
                  <div className="font-mono-tech text-[11px] tracking-widest uppercase text-olive">{s.subtitle}</div>
                  <h3 className="font-display font-light text-2xl sm:text-3xl text-caramel mt-2 leading-tight">{s.title}</h3>
                  <p className="text-caramel/75 mt-3 leading-relaxed">{s.brief}</p>
                  <span className="mt-5 inline-flex items-center gap-2 font-medium text-terracotta">
                    Explore the case study
                    <ArrowUpRight size={18} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Supporting web-design work */}
        <div className="mt-10 grid md:grid-cols-2 gap-6" data-testid="portfolio-supporting-row">
          {SUPPORTING_PORTFOLIO.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div
                className="group rounded-[1.4rem] overflow-hidden border border-border bg-ivory flex hover:-translate-y-1 transition-transform duration-300"
                data-testid={`portfolio-support-${i}`}
                data-cosmic="true"
              >
                <div className="w-32 sm:w-40 shrink-0 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <span className="font-mono-tech text-[10px] tracking-widest uppercase text-olive">{p.tag}</span>
                  <h4 className="font-display text-2xl text-caramel leading-tight mt-1">{p.title}</h4>
                  <p className="text-sm text-caramel/70 mt-2 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Modal study={active} onClose={() => setActive(null)} />
    </section>
  );
}
